// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Accordion, Box, Flex, Group, LoadingOverlay, Space, Stack, Text } from '@mantine/core';
import { FC, useContext, useState } from 'react';
import { IconDragDrop, IconDragDrop2, IconSection, IconTrashX } from '@tabler/icons';
import { i18n } from '@lingui/core';
import { useDrag, useDrop } from 'react-dnd-latest';
import { useElementSize } from '@mantine/hooks';
import { BundleHandlers, QuestionBundleDto, QuestionHandlers } from '../../../Types/QuestionnaireT';
import { EventRichInput, RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import { TiptapS } from '../../../Service/TiptapS';
import LinkText from '../../../Atoms/LinkText';
import BookitupAccordion from '../../../Atoms/BookitupAccordion';
import { openDeleteConfirmModal } from '../../../Atoms/DeleteConfirmModal';
import { QuestionBundleSkeleton } from '../../../Atoms/SkeletonItems';
import { isDefined } from '../../../Service/ValidationS';
import { replacePlaceholdersWithTranslations } from '../../../Service/Placeholder/PlaceholderResover';
import { WindowContext } from '../../../Service/Context/WindowContext';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import Question from './Question';

interface Props {
  bundle: QuestionBundleDto;
  bundleHandlers: BundleHandlers;
  questionHandlers: QuestionHandlers;
}

const QuestionBundle: FC<Props> = ({ bundle, bundleHandlers, questionHandlers }) => {
  const { lg } = useContext(WindowContext);
  const [loading, setLoading] = useState(false);
  const { isEventScope } = useEventScope();
  const { insertBundle, editBundle, deleteBundle, moveBundle } = bundleHandlers;
  const { insertQuestion } = questionHandlers;
  const { id: bundleId, title, description, questions } = bundle;
  const questionsLength = questions ? questions.length : 0;
  const { ref, width } = useElementSize();

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'questionBundle',
      canDrop: (item: QuestionBundleDto) => item.id !== bundleId,
      collect: (monitor: any) => ({
        isOver: monitor.isOver() && monitor.getItem().id !== bundleId,
        canDrop: !!monitor.canDrop(),
      }),
      drop: async (item: QuestionBundleDto) => {
        moveBundle(item.id, bundleId);
      },
    }),
    [bundleId],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'questionBundle',
      item: bundle,
      collect: (monitor: any) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [bundleId],
  );

  const insertQuestionHandler = async (index?: number) => {
    setLoading(true);
    await insertQuestion(bundleId, index);
    setLoading(false);
  };

  const deleteHandler = () =>
    openDeleteConfirmModal({
      title: i18n._('questionnaire.questionBundle.delete.confirm.head'),
      description: i18n._('questionnaire.questionBundle.delete.confirm'),
      onConfirm: () => deleteBundle(bundleId),
    });

  return (
    <div ref={drop} style={{ opacity: isDragging ? 0.5 : 1 }}>
      <Flex ref={ref} gap="xs" align="center">
        {lg && (
          <div ref={drag}>
            {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
            {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
          </div>
        )}
        <BookitupAccordion defaultValue="foo" sx={{ flexGrow: 1 }}>
          <Accordion.Item value="foo">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Accordion.Control>
                <Group>
                  <IconSection size={20} style={{ marginRight: 10, marginTop: -3 }} />
                  <Stack ref={ref} spacing={0}>
                    <Text className="overflow-ellipsis" sx={{ width: width - 200 }}>
                      {isDefined(title) ? replacePlaceholdersWithTranslations(i18n, title) : 'Abschnitt'}
                    </Text>
                    <Text size="xs" color="dimmed">
                      {questionsLength > 0
                        ? questionsLength === 1
                          ? '1 Frage'
                          : `${questionsLength} Fragen`
                        : 'Keine Fragen'}
                    </Text>
                  </Stack>
                </Group>
              </Accordion.Control>
              <div style={{ marginRight: 15 }}>
                <IconTrashX size={20} className="hooverable-icon" onClick={deleteHandler} />
              </div>
            </Box>
            <Accordion.Panel>
              {isEventScope && (
                <EventRichInput
                  label="Überschrift"
                  value={title ?? ''}
                  patchField={(htmlString) => editBundle(bundleId, { title: htmlString })}
                  menu={<DocumentBubbleMenu />}
                  styles={TiptapS.TextInputStyles}
                />
              )}
              {!isEventScope && (
                <RichInput
                  label="Überschrift"
                  value={title ?? ''}
                  patchField={(htmlString) => editBundle(bundleId, { title: htmlString })}
                  menu={<DocumentBubbleMenu />}
                  styles={TiptapS.TextInputStyles}
                />
              )}
              <Space h="xs" />
              {isEventScope && (
                <EventRichInput
                  label="Beschreibung (optional)"
                  value={description ?? ''}
                  patchField={(htmlString) => editBundle(bundleId, { description: htmlString })}
                  menu={<DocumentBubbleMenu />}
                  styles={TiptapS.TextInputStyles}
                />
              )}
              {!isEventScope && (
                <RichInput
                  label="Beschreibung (optional)"
                  value={description ?? ''}
                  patchField={(htmlString) => editBundle(bundleId, { description: htmlString })}
                  menu={<DocumentBubbleMenu />}
                  styles={TiptapS.TextInputStyles}
                />
              )}
              <Text size="lg" weight="bolder" mb="sm" mt="xl">
                Fragen
              </Text>
              <Box pos="relative">
                <LoadingOverlay visible={loading} overlayBlur={2} loaderProps={{ size: 'xl' }} />
                {(questions === null || questions.length === 0) && !loading && <QuestionBundleSkeleton />}
                <Stack pos="relative" mb="sm">
                  {questions &&
                    questions.map((q, idx) => (
                      <Question
                        key={q.id}
                        index={idx}
                        questionsLength={questions.length}
                        questionHandlers={questionHandlers}
                        setLoading={setLoading}
                        bundleId={bundleId}
                        questionDto={q}
                      />
                    ))}
                </Stack>
                {!loading && questionsLength === 0 && (
                  <LinkText onClick={() => insertQuestionHandler()}>+ Frage hinzufügen</LinkText>
                )}
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        </BookitupAccordion>
      </Flex>
      <LinkText mt="md" onClick={() => insertBundle()}>
        + Abschnitt hinzufügen
      </LinkText>
    </div>
  );
};

export default QuestionBundle;
